import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { Loader2, CheckCircle, AlertCircle, X, ClipboardList, XCircle, Trophy, RotateCcw } from 'lucide-react';
import {
  useStartQuizAttempt,
  useQuizAttemptData,
  useProcessQuizAttempt,
  getQuizAttemptReview,
  QuizQuestion,
  ReviewQuestion
} from '../../../api/quizAttemptApi';
import './QuizMoodleStyles.css';

// Strip Moodle screen-reader labels and mark "Clear my choice" rows before rendering
const sanitizeQuizHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild!;
  // Remove .accesshide elements (screen-reader labels)
  root.querySelectorAll('.accesshide').forEach(el => el.remove());
  // Remove <legend> elements (e.g. "Question N Answer" fieldset label)
  root.querySelectorAll('legend').forEach(el => el.remove());
  // Remove "Clear my choice" rows entirely
  root.querySelectorAll('input[type="radio"]').forEach(inputEl => {
    const input = inputEl as HTMLInputElement;
    if (input.value === '' || input.value === '-1') {
      const row = input.closest('div');
      if (row) row.remove();
    }
  });
  return root.innerHTML;
};

interface QuizContainerProps {
  contextId: string;
  quizId: number;
  quizName: string;
  maxGrade?: number;
  attemptsAllowed?: number; // 0 = unlimited
  onClose: () => void;
  onComplete?: () => void;
  headerColor?: string;
}

const QuizContainer: React.FC<QuizContainerProps> = ({
  contextId,
  quizId,
  quizName,
  maxGrade,
  attemptsAllowed,
  onClose,
  onComplete,
  headerColor,
}) => {
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [questionResults, setQuestionResults] = useState<Record<number, ReviewQuestion>>({});
  const [reviewScore, setReviewScore] = useState<number | null>(null);
  const [attemptsUsedCount, setAttemptsUsedCount] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const startAttemptMutation = useStartQuizAttempt();
  const processAttemptMutation = useProcessQuizAttempt();
  // Pass -1 to fetch all questions at once (no pagination)
  const { data: attemptData, isLoading, error } = useQuizAttemptData(contextId, attemptId, -1);

  // Start attempt on mount
  useEffect(() => {
    startAttemptMutation.mutate(
      { contextId, quizId },
      {
        onSuccess: (response) => {
          setAttemptId(response.data.attempt.id);
        }
      }
    );
  }, []);

  // Extract input values from Moodle HTML
  const captureAnswers = () => {
    const formData: Record<string, any> = {};
    const form = document.querySelector('#quiz-form');
    
    if (form) {
      const inputs = form.querySelectorAll('input, select, textarea');
      inputs.forEach((input: any) => {
        if (input.name) {
          if (input.type === 'checkbox') {
            if (input.checked) {
              if (!formData[input.name]) formData[input.name] = [];
              formData[input.name].push(input.value);
            }
          } else if (input.type === 'radio') {
            if (input.checked) formData[input.name] = input.value;
          } else {
            formData[input.name] = input.value;
          }
        }
      });
    }
    return formData;
  };

  const handleSubmitQuiz = async () => {
    // Validate all questions are answered
    const form = document.querySelector('#quiz-form');
    if (form) {
      const groups: Record<string, boolean> = {};
      form.querySelectorAll('input[type="radio"]').forEach((el: any) => {
        if (!el.name) return;
        if (!(el.name in groups)) groups[el.name] = false;
        if (el.checked && el.value !== '' && el.value !== '-1') groups[el.name] = true;
      });
      const unanswered = Object.values(groups).filter(answered => !answered).length;
      if (unanswered > 0) {
        setSubmitError(
          `${unanswered} question${unanswered > 1 ? 's' : ''} unanswered. Please answer all questions before submitting.`
        );
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    setSubmitError(null);
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirm(false);
    try {
        await processAttemptMutation.mutateAsync({
          contextId,
          attemptId: attemptId!,
          answers: captureAnswers(),
          finishAttempt: true
        });
        // Fetch review data to get per-question results and score
        try {
          const review = await getQuizAttemptReview(contextId, attemptId!);
          const results: Record<number, ReviewQuestion> = {};
          (review.data.questions || []).forEach(q => {
            results[q.slot] = q;
          });
          setQuestionResults(results);
          setReviewScore(review.data.sumgrades ?? null);
          setAttemptsUsedCount(review.data.attempt?.attempt ?? null);
        } catch {
          // Review fetch failed — still show submitted state without per-question details
        }
        setSubmitted(true);
        onComplete?.();
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        console.error('Error submitting quiz:', error);
        alert('Failed to submit quiz. Please try again.');
      }
  };

  const handleRetry = () => {
    setSubmitted(false);
    setQuestionResults({});
    setReviewScore(null);
    setAttemptsUsedCount(null);
    setSubmitError(null);
    setAttemptId(null);
    startAttemptMutation.mutate(
      { contextId, quizId },
      {
        onSuccess: (response) => {
          setAttemptId(response.data.attempt.id);
          scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    );
  };

  // Loading state
  if (startAttemptMutation.isPending || isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || startAttemptMutation.isError) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg border border-red-200 p-6 max-w-md">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <h3 className="text-lg font-semibold text-red-800">Failed to load quiz</h3>
          </div>
          <p className="text-sm text-red-700 mb-4">
            {error instanceof Error ? error.message : 'An error occurred while loading the quiz'}
          </p>
          <Button onClick={onClose} variant="outline" className="w-full">Close</Button>
        </div>
      </div>
    );
  }

  const questions = attemptData?.data.questions || [];

  return (
    <div ref={scrollContainerRef} className="fixed inset-0 z-100 bg-gray-50 overflow-y-auto">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 overflow-hidden border-b border-gray-200" style={{ background: '#F9FAFB', borderTop: `4px solid ${headerColor || '#374151'}` }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg p-2 shrink-0" style={{ background: headerColor ? `${headerColor}1A` : '#E5E7EB' }}>
              <ClipboardList className="h-5 w-5" style={{ color: headerColor || '#374151' }} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Quiz</p>
              <h1 className="text-base font-bold leading-tight text-gray-900">{quizName}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {submitted && reviewScore !== null && (
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1">
                <Trophy className="h-3.5 w-3.5 text-yellow-500" />
                <span className="text-sm font-semibold text-gray-800">
                  {reviewScore} / {maxGrade ?? '?'} marks
                </span>
              </div>
            )}
            {submitted && attemptsAllowed !== undefined && attemptsAllowed > 0 && attemptsUsedCount !== null && (
              <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1 text-xs text-gray-600 font-medium">
                {Math.max(0, attemptsAllowed - attemptsUsedCount)} attempt{Math.max(0, attemptsAllowed - attemptsUsedCount) !== 1 ? 's' : ''} remaining
              </div>
            )}
            {!submitted && attemptsAllowed !== undefined && attemptsAllowed > 0 && (
              <div className="text-xs text-gray-500 font-medium">
                {attemptsAllowed} attempt{attemptsAllowed !== 1 ? 's' : ''} allowed
              </div>
            )}
            <Button onClick={onClose} variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Questions ── */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <form id="quiz-form" className="space-y-5">
          {submitError && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 font-medium">{submitError}</p>
            </div>
          )}
          {questions.map((question: QuizQuestion) => {
            const result = questionResults[question.slot];
            const isCorrect = result?.state === 'gradedright';
            const isWrong = result?.state === 'gradedwrong' || result?.state === 'gradedpartial';
            return (
            <div
              key={question.slot}
              className={`bg-white rounded-xl border p-6 shadow-sm overflow-hidden ${
                submitted && isCorrect ? 'border-green-300' :
                submitted && isWrong ? 'border-red-300' :
                'border-gray-200'
              }`}
            >
              {/* Question header */}
              <div className={`flex items-center gap-3 px-6 py-3 -mx-6 -mt-6 mb-5 border-b ${
                submitted && isCorrect ? 'bg-green-50 border-green-100' :
                submitted && isWrong ? 'bg-red-50 border-red-100' :
                'bg-slate-50 border-gray-100'
              }`}>
                <span className="text-sm font-semibold text-gray-700">Question {question.number}</span>
                <span className="ml-auto text-xs font-medium text-slate-500 bg-white border border-gray-200 rounded-full px-3 py-0.5 shrink-0">
                  {question.maxmark === 1 ? '1 mark' : `${question.maxmark} marks`}
                </span>
              </div>
              {/* Question HTML from Moodle */}
              <div
                className="quiz-question-content"
                dangerouslySetInnerHTML={{ __html: sanitizeQuizHtml(question.html) }}
              />
              {/* Per-question result badge (shown after submission) */}
              {submitted && result && (
                isCorrect ? (
                  <div className="mt-4 flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 text-sm font-medium">
                    <CheckCircle className="h-4 w-4 shrink-0" />
                    Correct
                  </div>
                ) : isWrong ? (
                  <div className="mt-4 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-sm font-medium">
                    <XCircle className="h-4 w-4 shrink-0" />
                    Incorrect — review this question
                  </div>
                ) : null
              )}
            </div>
            );
          })}
        </form>

        {/* ── Submit / Retry / Close ── */}
        <div className="mt-8 flex justify-end">
          {submitted ? (() => {
            // Unlimited attempts (0) or still has remaining
            const remaining = attemptsAllowed === 0
              ? Infinity
              : attemptsAllowed !== undefined && attemptsUsedCount !== null
                ? Math.max(0, attemptsAllowed - attemptsUsedCount)
                : null;
            const canRetry = remaining === null ? false : remaining === Infinity || remaining > 0;
            return canRetry ? (
              <div className="flex gap-3">
                <Button onClick={onClose} variant="outline" size="lg">
                  <X className="h-4 w-4 mr-2" />Close
                </Button>
                <Button
                  onClick={handleRetry}
                  disabled={startAttemptMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  {startAttemptMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting...</>
                  ) : (
                    <><RotateCcw className="h-4 w-4 mr-2" />Retry Attempt</>
                  )}
                </Button>
              </div>
            ) : (
              <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700" size="lg">
                <X className="h-4 w-4 mr-2" />Close
              </Button>
            );
          })() : (
            <Button
              onClick={handleSubmitQuiz}
              disabled={processAttemptMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
              size="lg"
            >
              {processAttemptMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
              ) : (
                <><CheckCircle className="h-4 w-4 mr-2" />Submit Quiz</>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* ── Submit Confirmation Overlay ── */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 text-center mb-1">Submit Quiz?</h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              Once submitted, your answers cannot be changed. Make sure you've reviewed all questions.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowConfirm(false)}
                variant="outline"
                className="flex-1"
                size="lg"
              >
                Go Back
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                disabled={processAttemptMutation.isPending}
                className="flex-1 bg-green-600 hover:bg-green-700"
                size="lg"
              >
                {processAttemptMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                ) : (
                  'Submit'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizContainer;
